import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Target, 
  Eye, 
  Award, 
  Users, 
  CheckCircle,
  ArrowRight,
  Shield,
  TrendingUp,
  Clock,
  Heart
} from 'lucide-react';

export const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We maintain the highest ethical standards in all our dealings.',
    },
    {
      icon: TrendingUp,
      title: 'Excellence',
      description: 'We strive for excellence in every service we provide.',
    },
    {
      icon: Heart,
      title: 'Client First',
      description: 'Our clients success is our top priority.',
    },
    {
      icon: Clock,
      title: 'Reliability',
      description: 'Consistent, timely, and dependable service delivery.',
    },
  ];

  const team = [
    {
      name: 'John Anderson',
      role: 'Founder & CEO',
      description: 'Over 20 years of experience in tax consulting.',
    },
    {
      name: 'Sarah Mitchell',
      role: 'Head of Tax Services',
      description: 'CPA with expertise in corporate taxation.',
    },
    {
      name: 'Michael Chen',
      role: 'Senior Consultant',
      description: 'Specialist in GST and indirect taxation.',
    },
    {
      name: 'Emily Roberts',
      role: 'Compliance Manager',
      description: 'Expert in regulatory compliance and audits.',
    },
  ];

  const milestones = [
    { year: '2020', event: 'Company Founded', description: 'Started with a vision to simplify taxation' },
    { year: '2021', event: 'Expanded Services', description: 'Added GST and business registration' },
    { year: '2023', event: '10K Clients', description: 'Reached milestone of 10,000 satisfied clients' },
    { year: '2025', event: '25K+ Clients', description: 'Serving over 25,000 clients nationwide' },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        data-testid="about-hero"
        className="relative py-20 md:py-32 overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
              About Us
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-6 leading-tight">
              Your Trusted Partner in{' '}
              <span className="text-gradient">Tax Excellence</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              For over 5 years, TaxCompany has been helping individuals and businesses 
              navigate the complex world of taxation. Our commitment to excellence and 
              client satisfaction has made us a trusted name in the industry.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section 
        data-testid="mission-vision"
        className="py-20 md:py-32 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div 
              data-testid="mission-card"
              className="bg-navy-800 border border-white/5 rounded-2xl p-8 md:p-10"
            >
              <div className="w-16 h-16 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-blue-400" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-white mb-4">Our Mission</h2>
              <p className="text-slate-400 leading-relaxed">
                To simplify taxation for every individual and business by providing 
                accurate, efficient, and affordable tax services. We aim to empower 
                our clients with knowledge and tools to make informed financial decisions.
              </p>
            </div>
            <div 
              data-testid="vision-card"
              className="bg-navy-800 border border-white/5 rounded-2xl p-8 md:p-10"
            >
              <div className="w-16 h-16 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-sky-400" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-white mb-4">Our Vision</h2>
              <p className="text-slate-400 leading-relaxed">
                To become the most trusted and innovative tax services company, 
                recognized for our expertise, integrity, and commitment to client 
                success. We envision a world where tax compliance is simple and accessible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section 
        data-testid="values-section"
        className="py-20 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-blue-400 font-medium mb-4 block">Our Values</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              What Drives Us
            </h2>
            <p className="text-slate-400">
              Our core values guide everything we do and shape how we serve our clients.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl p-6 text-center card-hover group"
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <value.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline/History */}
      <section 
        data-testid="history-section"
        className="py-20 md:py-32 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-blue-400 font-medium mb-4 block">Our Journey</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Milestones Along the Way
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-white/10 transform -translate-x-1/2" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={milestone.year}
                  data-testid={`milestone-${index}`}
                  className={`flex flex-col md:flex-row gap-8 items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-navy-800 border border-white/5 rounded-2xl p-6 inline-block">
                      <span className="text-blue-400 font-heading font-bold text-2xl">
                        {milestone.year}
                      </span>
                      <h3 className="font-heading font-semibold text-white mt-2">
                        {milestone.event}
                      </h3>
                      <p className="text-slate-400 text-sm mt-1">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-blue-500 border-4 border-navy-900 z-10" />
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section 
        data-testid="team-section"
        className="py-20 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-blue-400 font-medium mb-4 block">Our Team</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Meet the Experts
            </h2>
            <p className="text-slate-400">
              Our team of certified professionals brings decades of combined experience.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <div
                key={member.name}
                data-testid={`team-member-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl overflow-hidden card-hover group"
              >
                <div className="aspect-square overflow-hidden bg-gradient-to-br from-blue-500/20 to-sky-500/20 flex items-center justify-center">
                  <span className="text-6xl font-heading font-bold text-blue-400">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-semibold text-lg text-white">
                    {member.name}
                  </h3>
                  <p className="text-blue-400 text-sm mb-2">{member.role}</p>
                  <p className="text-slate-400 text-sm">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section 
        data-testid="about-stats"
        className="py-20 md:py-32 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Numbers That Speak for Themselves
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Our track record of success is reflected in the numbers. We take pride in 
                every client served and every return filed accurately.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div data-testid="about-stat-0" className="bg-navy-900 border border-white/5 rounded-xl p-6">
                  <p className="font-heading font-bold text-3xl text-blue-400">150K+</p>
                  <p className="text-slate-400">Projects Completed</p>
                </div>
                <div data-testid="about-stat-1" className="bg-navy-900 border border-white/5 rounded-xl p-6">
                  <p className="font-heading font-bold text-3xl text-blue-400">25K+</p>
                  <p className="text-slate-400">Happy Clients</p>
                </div>
                <div data-testid="about-stat-2" className="bg-navy-900 border border-white/5 rounded-xl p-6">
                  <p className="font-heading font-bold text-3xl text-blue-400">99.9%</p>
                  <p className="text-slate-400">Accuracy Rate</p>
                </div>
                <div data-testid="about-stat-3" className="bg-navy-900 border border-white/5 rounded-xl p-6">
                  <p className="font-heading font-bold text-3xl text-blue-400">24/7</p>
                  <p className="text-slate-400">Support Available</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-sky-500/10 rounded-3xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Team collaboration"
                className="relative rounded-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section 
        data-testid="about-cta"
        className="py-20 md:py-32"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-sky-500/10 border border-white/10 rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                Ready to Work With Us?
              </h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Join thousands of satisfied clients who trust us with their tax needs.
              </p>
              <Link to="/contact">
                <Button 
                  data-testid="about-cta-button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 text-lg"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
